import { ApiError } from '../../app/api/client';

const FRIENDLY_AI_ERRORS: Record<string, string> = {
  ai_api_key_invalid: 'AI extraction is not configured correctly on this server.',
  ai_api_key_missing: 'AI extraction is not configured on this server.',
  ai_disabled: 'AI extraction is disabled on this server.',
  ai_file_too_large:
    'This PDF exceeds the server AI transmission limit. The manual editor is still available.',
  ai_invalid_import_state:
    'AI extraction is not available in this import state. The draft was not changed.',
  ai_page_count_required:
    'Process the PDF locally before requesting AI extraction. The draft was not changed.',
  ai_page_limit:
    'This PDF exceeds the server AI page limit. The manual editor is still available.',
  ai_pdf_required:
    'AI document extraction accepts PDF imports only. The manual editor is still available.',
  ai_provider_internal_error:
    'OpenAI extraction could not be completed safely. The draft was not changed.',
  ai_provider_point_limit:
    'The AI suggestion contained too many points. The draft was not changed.',
  ai_run_conflict:
    'Another AI request is already active for this import. The draft was not changed.',
  ai_run_limit:
    'This import has reached its configured AI request limit. Existing review data and the manual editor remain available.',
  ai_source_run_limit:
    'This source document has reached its configured AI request limit, including duplicate uploads. Existing review data and the manual editor remain available.',
  ai_workspace_run_limit:
    'This workspace has reached its rolling daily AI request limit. Use the manual editor or try again after the quota window advances.',
  ai_incomplete_provider_output:
    'OpenAI did not complete the extraction. The manual editor is still available.',
  ai_invalid_provider_output:
    'OpenAI returned an unreadable suggestion. The draft was not changed.',
  ai_invalid_provider_json:
    'OpenAI returned a response that could not be read as structured data. The draft was not changed.',
  ai_invalid_provider_schema:
    'OpenAI returned curve data with missing or invalid fields. The draft was not changed.',
  ai_invalid_provider_calibration:
    'OpenAI did not identify usable printed axis ticks for calibration. The draft was not changed. Check the graph selection or set the ticks manually.',
  ai_crop_geometry_mismatch:
    'OpenAI returned trace positions outside the selected graph. The draft was not changed. Check the graph box before trying again.',
  ai_page_scope_mismatch:
    'OpenAI returned data from an unselected page. The draft was not changed.',
  ai_unsupported_schema_version:
    'OpenAI returned curve data in an unsupported format version. The draft was not changed.',
  ai_provider_output_token_limit:
    'OpenAI reached the response length limit before finishing the extraction. The draft was not changed.',
  ai_provider_http_error:
    'OpenAI could not complete the extraction request. The draft was not changed.',
  ai_provider_network_error:
    'The server could not reach OpenAI. The draft was not changed.',
  ai_provider_refusal:
    'OpenAI declined to process this PDF. The draft was not changed.',
  ai_provider_response_too_large:
    'The AI response exceeded the configured safety limit. The draft was not changed.',
  ai_provider_timeout:
    'OpenAI took too long. Nothing was changed. Try again.',
  ai_unavailable: 'AI extraction is unavailable. The manual editor is still available.',
  ai_unexpected_provider_model:
    'The provider returned a model outside the server allowlist. The draft was not changed.',
  discard_source_mismatch:
    'The displayed filename or source hash is stale. Nothing was deleted; close this confirmation and reopen the import before trying again.',
  protected_library_history:
    'This import is retained as part of reviewed-library copy history. Nothing was deleted; archive an unverified copy instead.',
  protected_library_master:
    'This import supports an immutable reviewed-library master. Nothing was deleted; its source and review history must be retained.',
};

const SENSITIVE_PATTERNS: Array<[RegExp, string]> = [
  [/data:application\/pdf;base64,[a-z0-9+/=]+/giu, '[PDF content redacted]'],
  [
    /((?:content|pdf)_base64["']?\s*[:=]\s*["']?)[a-z0-9+/=]{16,}/giu,
    '$1[PDF content redacted]',
  ],
  [/(authorization\s*:\s*bearer\s+)[^\s,;]+/giu, '$1[redacted]'],
  [/\bsk-[a-z0-9_-]{8,}\b/giu, '[credential redacted]'],
  [
    /((?:openai[_ -]?)?api[_ -]?key\s*[=:]\s*)[^\s,;]+/giu,
    '$1[redacted]',
  ],
  [/(\/home\/|\/users\/)[^\s,;]+/giu, '[local path redacted]'],
  [/\b[a-z]:\\[^\s,;]+/giu, '[local path redacted]'],
];

function redact(value: string): string {
  let safe = value;
  for (const [pattern, replacement] of SENSITIVE_PATTERNS) {
    safe = safe.replace(pattern, replacement);
  }
  return safe.length > 600 ? `${safe.slice(0, 597)}…` : safe;
}

function withAiSupportReference(message: string, error: ApiError): string {
  const reference = error.correlationId;
  if (
    reference === null ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
      reference,
    )
  ) {
    return message;
  }
  return `${message} Support reference: ${reference}.`;
}

export function curveIssueTypes(error: unknown): Set<string> {
  if (!(error instanceof ApiError) || error.details === null) return new Set();
  return new Set(error.details.map((detail) => detail.issue_type));
}

export function isProtectedDiscardError(error: unknown): boolean {
  const issues = curveIssueTypes(error);
  return [
    'active_processing_job',
    'protected_ai_audit_history',
    'protected_curve_references',
    'protected_historical_import',
    'protected_library_history',
    'protected_library_master',
    'protected_verified_import',
  ].some((issue) => issues.has(issue));
}

export function readableCurveError(
  error: unknown,
  fallback = 'The requested operation failed.',
): string {
  if (error instanceof ApiError) {
    const issue = error.details?.[0]?.issue_type;
    if (issue !== undefined && FRIENDLY_AI_ERRORS[issue] !== undefined) {
      const message = FRIENDLY_AI_ERRORS[issue];
      return issue.startsWith('ai_')
        ? withAiSupportReference(message, error)
        : message;
    }
    if (error.message in FRIENDLY_AI_ERRORS) {
      const message = FRIENDLY_AI_ERRORS[error.message] ?? fallback;
      return error.message.startsWith('ai_')
        ? withAiSupportReference(message, error)
        : message;
    }
    if (issue?.startsWith('ai_') || error.message.startsWith('ai_')) {
      return withAiSupportReference(
        'AI extraction could not be completed. The draft was not changed; use the manual editor or try again later.',
        error,
      );
    }
    return redact(error.message);
  }
  if (error instanceof Error) return redact(error.message);
  return fallback;
}
